import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DocSidebar from './components/DocSidebar';
import DocContent from './components/DocContent';
import TableOfContents from './components/TableOfContents';
import { apiDocStyles, getResponsiveStyles } from './styles/apiDocStyles';
import apiDataKo from './data/apiData.ko.json';
import apiDataEn from './data/apiData.en.json';

const ApiDocumentPage = () => {
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial state from URL params
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || null);
  const [selectedEndpoint, setSelectedEndpoint] = useState(searchParams.get('endpoint') || null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef(null);

  // Select API data based on language
  const apiData = i18n.language === 'ko' ? apiDataKo : apiDataEn;

  // Get endpoint for TOC
  const category = apiData.categories.find(c => c.id === selectedCategory);
  const endpoint = category?.endpoints.find(e => e.id === selectedEndpoint);

  // Handle responsive
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setLangDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Language change handler
  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setLangDropdownOpen(false);
  };

  // Update URL when selection changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedEndpoint) params.set('endpoint', selectedEndpoint);
    setSearchParams(params, { replace: true });
  }, [selectedCategory, selectedEndpoint, setSearchParams]);

  // Navigation handler
  const handleNavigate = (categoryId, endpointId) => {
    setSelectedCategory(categoryId);
    setSelectedEndpoint(endpointId);
    // Scroll to top on navigation
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const responsiveStyles = getResponsiveStyles(isMobile);

  return (
    <div style={responsiveStyles.pageContainer}>
      {/* Sidebar */}
      <DocSidebar
        apiData={apiData}
        selectedCategory={selectedCategory}
        selectedEndpoint={selectedEndpoint}
        onSelectCategory={setSelectedCategory}
        onSelectEndpoint={setSelectedEndpoint}
        isMobile={isMobile}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main Content */}
      <main style={apiDocStyles.mainContent}>
        {/* Language Dropdown */}
        <div style={langDropdownStyles.container} ref={langDropdownRef}>
          <button
            style={langDropdownStyles.button}
            onClick={() => setLangDropdownOpen(!langDropdownOpen)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ marginRight: '6px' }}>
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" strokeWidth="2"/>
            </svg>
            {i18n.language === 'ko' ? '한국어' : 'English'}
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              style={{
                marginLeft: '6px',
                transform: langDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}
            >
              <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {langDropdownOpen && (
            <div style={langDropdownStyles.menu}>
              <button
                style={{
                  ...langDropdownStyles.menuItem,
                  backgroundColor: i18n.language === 'ko' ? '#EDF4FE' : 'transparent',
                  color: i18n.language === 'ko' ? '#1A62EA' : '#374151'
                }}
                onClick={() => handleLanguageChange('ko')}
              >
                한국어
                {i18n.language === 'ko' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke="#1A62EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
              <button
                style={{
                  ...langDropdownStyles.menuItem,
                  backgroundColor: i18n.language === 'en' ? '#EDF4FE' : 'transparent',
                  color: i18n.language === 'en' ? '#1A62EA' : '#374151'
                }}
                onClick={() => handleLanguageChange('en')}
              >
                English
                {i18n.language === 'en' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke="#1A62EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flex: 1 }}>
          <DocContent
            apiData={apiData}
            selectedCategory={selectedCategory}
            selectedEndpoint={selectedEndpoint}
            onNavigate={handleNavigate}
            isMobile={isMobile}
          />

          {/* TOC - only show on endpoint detail page */}
          {!isMobile && selectedEndpoint && (
            <TableOfContents endpoint={endpoint} />
          )}
        </div>
      </main>

      {/* Mobile Menu Button */}
      {isMobile && (
        <button
          style={responsiveStyles.mobileMenuButton}
          onClick={() => setSidebarOpen(true)}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}
    </div>
  );
};

const langDropdownStyles = {
  container: {
    position: 'absolute',
    top: '16px',
    right: '3vw',
    zIndex: 100
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 14px',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s ease'
  },
  menu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '4px',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    overflow: 'hidden',
    minWidth: '120px'
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '10px 14px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease'
  }
};

export default ApiDocumentPage;
